import React from 'react';

import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { styled } from '@mui/material';

import { useDiscoverContext } from '~/context/DiscoverContext';
import { useTenantContext } from '~/context/TenantProvider';
import { GET_HAS_NEWS } from '~/graphql/local';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import TabItem from '~/pages/DiscoverPage/components/TabItem/TabItem';
import { HITS_TYPES } from '~/pages/const';
import {
  DISCOVER_COURSES_ROUTE,
  DISCOVER_GLOSSARY_ROUTE,
  DISCOVER_INSIGHTS_ROUTE,
  DISCOVER_PATHWAYS_ROUTE,
  DISCOVER_SEARCH_ROUTE,
  DISCOVER_VIDEOS_ROUTE,
} from '~/routes';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '~/theme';
import { Query } from '~/types';
import { TenantsRefs } from '~/utils/tenantsConfig';

interface TabsProps {
  selectedItem?: string;
  refine?: Function;
}

const Tabs: React.FC<TabsProps> = ({ selectedItem, refine }) => {
  const { t } = useTenantTranslation();

  const { searchState } = useDiscoverContext();
  const searchQuery = searchState?.query || '';
  const { tenant } = useTenantContext();

  const { data } = useQuery<Pick<Query, 'hasNews'>>(GET_HAS_NEWS);
  const hasNews = data?.hasNews;

  return (
    <Wrapper>
      <TabItem
        data-testid="allTab"
        value=""
        label={t('page.discover.all')}
        selectedItem={selectedItem}
        handleSelect={refine}
        link={`${DISCOVER_SEARCH_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`}
      />
      <TabItem
        data-testid="videosTab"
        label={t('page.discover.videoModules')}
        value={HITS_TYPES.VIDEO}
        selectedItem={selectedItem}
        handleSelect={refine}
        link={`${DISCOVER_VIDEOS_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`}
      />
      <TabItem
        data-testid="pathwaysTab"
        label={t('page.discover.pathways')}
        value={HITS_TYPES.PATHWAY}
        selectedItem={selectedItem}
        handleSelect={refine}
        link={`${DISCOVER_PATHWAYS_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`}
      />
      {hasNews && (
        <TabItem
          data-testid="newsTab"
          label={t('page.discover.text')}
          value={HITS_TYPES.NEWS}
          selectedItem={selectedItem}
          handleSelect={refine}
          link={`${DISCOVER_INSIGHTS_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`}
        />
      )}
      {tenant?.name !== TenantsRefs.Ssku && (
        <TabItem
          data-testid="glossaryTab"
          label={t('page.discover.glossary')}
          value={HITS_TYPES.GLOSSARY}
          selectedItem={selectedItem}
          handleSelect={refine}
          link={`${DISCOVER_GLOSSARY_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`}
        />
      )}
      {tenant?.name === TenantsRefs.Ssku && (
        <TabItem
          data-testid="coursesTab"
          label={t('page.discover.certificatePrograms')}
          value={HITS_TYPES.COURSE}
          selectedItem={selectedItem}
          handleSelect={refine}
          link={`${DISCOVER_COURSES_ROUTE}${searchQuery ? '?s=' + searchQuery : ''}`}
        />
      )}
    </Wrapper>
  );
};

export default Tabs;

const Wrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  & div,
  & a {
    margin-right: ${SMALL_MARGIN_PX};

    &:last-child {
      margin-right: 0;
    }

    ${({ theme }) => theme.breakpoints.up('sm')} {
      margin-right: ${MIDDLE_MARGIN_PX};
    }
  }
`;
